'use client';

import { useEffect, useCallback } from 'react';
import Link from 'next/link';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import { ArrowLeft, ArrowRight, MoreHorizontal } from '@1debit/icons';
import { useGreenhouseSearch } from '@/hooks/useGreenhouseSearch';

interface CareerSearchPaginationContentProps {
  totalPages: number;
  currentPage: number;
  paginationRange: (string | number)[];
}

const CareerSearchPagination = () => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const { totalPages, currentPage, paginationRange } = useGreenhouseSearch();

  useEffect(() => {
    if (totalPages > 0 && (currentPage < 1 || currentPage > totalPages)) {
      const clampedPage = Math.min(Math.max(1, currentPage), totalPages);
      const params = new URLSearchParams(searchParams.toString());
      params.set('page', clampedPage.toString());
      router.replace(`${pathname}?${params.toString()}`);
    }
  }, [currentPage, totalPages, pathname, searchParams, router]);

  if (totalPages <= 1) return null;

  return (
    <CareerSearchPaginationContent
      totalPages={totalPages}
      currentPage={currentPage}
      paginationRange={paginationRange}
    />
  );
};

const CareerSearchPaginationContent = ({
  totalPages,
  currentPage,
  paginationRange,
}: CareerSearchPaginationContentProps) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const createPageURL = useCallback(
    (page: number | string) => {
      const targetPage = Math.min(Math.max(1, Number(page)), totalPages);
      const params = new URLSearchParams(searchParams.toString());
      params.set('page', targetPage.toString());
      return `${pathname}?${params.toString()}`;
    },
    [pathname, searchParams, totalPages],
  );

  if (currentPage < 1 || currentPage > totalPages) {
    return null;
  }

  const isFirstPage = currentPage === 1;
  const isLastPage = currentPage === totalPages;

  return (
    <nav className="flex justify-center items-end mt-20" aria-label="Pagination">
      <Link
        href={isFirstPage ? '#' : createPageURL(currentPage - 1)}
        aria-disabled={isFirstPage}
        tabIndex={isFirstPage ? -1 : 0}
        className={`font-semibold mr-8 ${isFirstPage ? 'pointer-events-none opacity-50' : 'hover:underline'}`}>
        <ArrowLeft className="inline-block mr-1" />
        Previous
      </Link>

      {paginationRange.map((page, index) => {
        if (page === '...') {
          return (
            <span key={`dot-${index}`} className="font-semibold px-3 py-2">
              <MoreHorizontal />
            </span>
          );
        }

        const isActive = page === currentPage;
        return (
          <Link
            key={index}
            href={isActive ? '#' : createPageURL(page)}
            aria-current={isActive ? 'page' : undefined}
            tabIndex={isActive ? -1 : 0}
            className={`font-semibold mx-3 ${isActive ? 'pointer-events-none opacity-50' : 'hover:underline'}`}>
            {page}
          </Link>
        );
      })}

      <Link
        href={isLastPage ? '#' : createPageURL(currentPage + 1)}
        aria-disabled={isLastPage}
        tabIndex={isLastPage ? -1 : 0}
        className={`font-semibold ml-8 ${isLastPage ? 'pointer-events-none opacity-50' : 'hover:underline'}`}>
        Next
        <ArrowRight className="inline-block ml-1" />
      </Link>
    </nav>
  );
};

export default CareerSearchPagination;
